// useBookingSearch — TanStack Query hook for the Booking Search tab on
// the System Lookup page. Hits GET /collections/search/bookings which
// fans out a free-text search across bookings, pending_bookings, and
// receipts (UNION ALL — see server/.../collectionsSearch).
//
// The hook is `enabled`-gated: callers pass `enabled=false` until the
// user submits a query that's at least 2 characters long.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';

export type BookingSearchRecordType = 'booking' | 'pending' | 'receipt';

export interface BookingSearchResult {
  enc_id: string;
  record_type: BookingSearchRecordType;
  status: string;
  customer_name: string;
  collection_date: string | null;
  notes: string | null;
  driver_notes: string | null;
  additional_information: string | null;
  created_at: string;
}

export interface BookingSearchResponse {
  results: BookingSearchResult[];
  total: number;
  query: string;
}

interface BookingSearchParams {
  q: string;
  includeCompleted: boolean;
  enabled: boolean;
}

export function useBookingSearch(params: BookingSearchParams) {
  const trimmed = params.q.trim();
  return useQuery<BookingSearchResponse>({
    queryKey: ['booking-search', trimmed, params.includeCompleted] as const,
    queryFn: async () => {
      const search = new URLSearchParams();
      search.set('q', trimmed);
      if (params.includeCompleted) search.set('include_completed', 'true');
      const res = await apiClient.get<BookingSearchResponse>(
        `${endpoints.collections.searchBookings}?${search.toString()}`,
      );
      return res.data;
    },
    enabled: params.enabled && trimmed.length >= 2,
    staleTime: 30_000,
  });
}
